import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { L10nTranslatePipe } from 'angular-l10n';
import { IndexedDbService } from '@miq/platform';
import { COMMON_CONSTANTS, type ReportCustomData } from '@miq/shared-models';
import { ExpandableResource } from '@miq/utils';
import { DataCellFormatterPipe } from '../../../pipes/data-cell-formatter.pipe';
import { MiqTableCheckboxComponent } from '../../../vendors/table-checkbox.component';
import { makeSimpleFormGroup } from '../../../utils/simple-form-group';
import { navigateToEntity } from '../../../utils/navigate-to-entity';
import { IndentDirective } from './indent.directive';
import { SubtitleCellFormaterPipe } from './subtitle-cell-formater.pipe';

/**
 * L2-S5: moved verbatim out of
 * `@shared/components/tree-data-table/tree-data-row/tree-data-row.component`, which is now a
 * re-export shim. Rendered only by {@link TreeDataTableComponent}.
 *
 * Two host edges were inverted rather than followed into the library:
 *   - `SimpleFormGroupService.makeGroup` -> the pure `makeSimpleFormGroup` in lib/utils. The
 *     service is an `HttpMethods` subclass that also fetches geo markets; only the pure map was
 *     ever used here, and the host service now delegates to the same function.
 *   - `navigateToEntity` -> lib/utils/navigate-to-entity. Zero-dependency `window.open`, moved so
 *     the library does not import `@records`; the records constants file re-exports it.
 *
 * The host version imported the SAME pipe twice — `./data-cell-formatter.pipe` (used only to
 * `new` it in `ngOnInit`) and `../../../pipe/data-cell-formatter.pipe` (aliased into `imports`).
 * Both were already shims for @miq/platform-ui's `DataCellFormatterPipe`, so this file uses one
 * import for both roles.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  /* tslint:disable */
  selector: '[miq-tree-data-row]',
  /* tslint:enable */
  templateUrl: './tree-data-row.component.html',
  styleUrl: './tree-data-row.component.scss',
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatHint,
    MiqTableCheckboxComponent,
    IndentDirective,
    MatIcon,
    DataCellFormatterPipe,
    SubtitleCellFormaterPipe,
    L10nTranslatePipe,
  ],
})
export class TreeDataRowComponent implements OnInit {
  row = input<ExpandableResource<any>>();
  columns = input<any[]>([]);
  /**
   * prev: it sends the row snapshot
   * next: it sends the changed data (editable fields only)
   */
  rowChange = output<any>();
  toggle = output<any>();
  form: FormGroup;
  private readonly fb = inject(FormBuilder);
  private readonly indexedDbService = inject(IndexedDbService);
  ngOnInit() {
    const dataTransformService = new DataCellFormatterPipe();
    const editableFields = this.columns()
      .filter(({ editable }) => editable)
      .map(props => ({ ...props, value: dataTransformService.transform(this.row(), props) }));
    const controls = makeSimpleFormGroup(editableFields);
    this.form = this.fb.group(controls);
  }
  clicked(row, col) {
    const { rowClick, field } = col;
    if (rowClick && !row.editMode) {
      return !row.isContainer
        ? field === 'associatedEntityId' && row.associatedEntityId
          ? navigateToEntity(row)
          : this.navigateToReports(row)
        : this.toggle.emit(row);
    }
  }
  toggleRow(row) {
    return row.isContainer ? this.toggle.emit(row) : null;
  }
  navigateToReports(row) {
    this.indexedDbService.delete(COMMON_CONSTANTS.CURRENT_DOWNLOAD_TRANS_ID);
    this.indexedDbService.delete(COMMON_CONSTANTS.REPORT_CUSTOM_DATA);
    const customData: ReportCustomData = { entityType: row.entity, entityId: row.id };
    this.indexedDbService.set(COMMON_CONSTANTS.REPORT_CUSTOM_DATA, { reportId: row.id, ...customData });
    window.open(`/reports/${row.id}`);
  }
  submit() {
    return this.form.valid ? this.rowChange.emit({ prev: this.row(), next: this.form.value }) : null;
  }
}
